import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Loader2, ExternalLink, AlertTriangle, Columns } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../ui/dialog';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { ColorImage } from '../ColorImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompareRow = ({ row }) => (
  <div className={`grid grid-cols-[1fr_1fr_minmax(0,1.4fr)] gap-3 items-center p-2 rounded-lg border ${row.missing_cdn || row.gallery_mismatch ? 'border-amber-200 bg-amber-50/40' : 'border-neutral-200 bg-white'}`} data-testid={`compare-row-${row.id}`}>
    <div className="flex items-center gap-2">
      <div className="w-20 h-20 rounded-md overflow-hidden border border-neutral-200 bg-neutral-100 flex-shrink-0">
        {row.stored_url ? <ColorImage src={row.stored_url} alt={row.name} w={256} className="w-full h-full" /> : <div className="w-full h-full" />}
      </div>
      <span className="text-[10px] text-neutral-500">Stored swatch</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-20 h-20 rounded-md overflow-hidden border border-neutral-200 bg-neutral-100 flex-shrink-0 flex items-center justify-center">
        {row.cdn_url
          ? <img src={row.cdn_url} alt={`${row.name} original`} loading="lazy" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
          : <AlertTriangle className="w-5 h-5 text-amber-500" />}
      </div>
      <span className="text-[10px] text-neutral-500">{row.cdn_url ? 'Original CDN' : 'No CDN link'}</span>
    </div>
    <div className="min-w-0">
      <p className="text-xs font-semibold text-neutral-900 truncate">{row.name}</p>
      <p className="text-[10px] text-neutral-500 truncate">{row.code}{row.material ? ` · ${row.material}` : ''}</p>
      {row.gallery_mismatch && <p className="text-[10px] text-amber-700">Gallery image differs from collection swatch</p>}
      {row.cdn_url && (
        <a href={row.cdn_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-600 hover:underline inline-flex items-center gap-0.5">
          <ExternalLink className="w-2.5 h-2.5" /> open original
        </a>
      )}
    </div>
  </div>
);

export const ColorCompareDialog = ({ collection, open, onOpenChange, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(false);

  useEffect(() => {
    if (!open || !collection?.id) return;
    setLoading(true);
    setData(null);
    axios.get(`${API_URL}/api/admin/fabric-collections/collections/${collection.id}/compare`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => setData({ colors: [], total: 0, missing_cdn: 0, gallery_mismatches: 0 }))
      .finally(() => setLoading(false));
  }, [open, collection?.id, token]);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (data?.colors || []).filter(r =>
      (!onlyIssues || r.missing_cdn || r.gallery_mismatch) &&
      (!q || r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q) || (r.material || '').toLowerCase().includes(q))
    );
  }, [data, filter, onlyIssues]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="color-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Columns className="w-5 h-5 text-sky-600" /> Color Comparison — {collection?.name}</DialogTitle>
          <DialogDescription className="text-xs">Stored swatch (left) vs. the brand's original catalog image (right)</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-neutral-100 text-neutral-700" data-testid="compare-total">{data?.total ?? 0} colors</span>
              <span className={`px-2 py-1 rounded ${data?.missing_cdn ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`} data-testid="compare-missing-cdn">{data?.missing_cdn ?? 0} without CDN original</span>
              <span className={`px-2 py-1 rounded ${data?.gallery_mismatches ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`} data-testid="compare-gallery-mismatch">{data?.gallery_mismatches ?? 0} gallery mismatches</span>
              <div className="flex-1" />
              <Button size="sm" variant={onlyIssues ? 'default' : 'outline'} className={`h-7 text-xs ${onlyIssues ? 'bg-amber-600 hover:bg-amber-700' : ''}`} onClick={() => setOnlyIssues(v => !v)} data-testid="compare-only-issues">Issues only</Button>
              <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter by name / code / material" className="h-7 text-xs w-56" data-testid="compare-filter" />
            </div>
            <div className="overflow-y-auto mt-3 space-y-1.5 pr-1 flex-1" data-testid="compare-list">
              {rows.length === 0
                ? <p className="text-sm text-neutral-400 text-center py-10">No colors to show</p>
                : rows.map(r => <CompareRow key={r.id || r.code} row={r} />)}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ColorCompareDialog;
